import React, { useCallback, useEffect, useState } from "react";
import Drawer from "@mui/material/Drawer";
import IconButton from "@mui/material/IconButton";
import CloseIcon from "@mui/icons-material/Close";
import Checkbox from "@mui/material/Checkbox";
import Select from "@mui/material/Select";
import MenuItem from "@mui/material/MenuItem";
import FormControl from "@mui/material/FormControl";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import AutorenewIcon from "@mui/icons-material/Autorenew";
import BuildIcon from "@mui/icons-material/Build";
import NotificationsNoneIcon from "@mui/icons-material/NotificationsNone";
import instance from "../../axios";
import Button from "../../Components/common/Button";
import Loader from "../Loader";
import moment from "moment";
import "./NotificationDrawer.scss";

const READ_FILTER = {
  ALL: 0,
  READ: 1,
  UNREAD: 2,
};

const PAGE_LIMIT = 5;
const DEFAULT_ALERT_COLOR = "#5c6bc0";

const getNotificationTypeKey = (item) =>
  (
    item?.attributes?.functionalType ||
    item?.type ||
    item?.name ||
    ""
  ).toLowerCase();

const getNotificationTitle = (item) =>
  item?.name || item?.type || "Notification";

const getNotificationDescription = (item) => item?.description || "";

const getAlertColor = (item) =>
  item?.attributes?.alertColor || DEFAULT_ALERT_COLOR;

const hexToRgba = (hex, alpha = 1) => {
  if (!hex || typeof hex !== "string") {
    return `rgba(92, 107, 192, ${alpha})`;
  }

  const cleaned = hex.replace("#", "");
  const fullHex =
    cleaned.length === 3
      ? cleaned
          .split("")
          .map((char) => char + char)
          .join("")
      : cleaned;

  if (fullHex.length !== 6) {
    return `rgba(92, 107, 192, ${alpha})`;
  }

  const num = parseInt(fullHex, 16);
  const r = (num >> 16) & 255;
  const g = (num >> 8) & 255;
  const b = num & 255;
  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
};

const getNotificationCardStyle = (item) => {
  const accentColor = getAlertColor(item);
  return {
    "--notification-accent": accentColor,
    backgroundColor: hexToRgba(accentColor, 0.08),
    borderColor: hexToRgba(accentColor, 0.22),
  };
};

const getNotificationDate = (item) => {
  const date =
    item?.createdAt ||
    item?.notificationTime ||
    item?.date ||
    item?.eventTime ||
    item?.actionTime;
  return date ? moment(date).format("YYYY-MM-DD HH:mm") : "";
};

const isNotificationRead = (item) =>
  item?.markRead === true ||
  item?.isRead === true ||
  item?.read === true ||
  item?.read === 1;

const NotificationIcon = ({ item }) => {
  const type = getNotificationTypeKey(item);

  if (type.includes("maintenance") || type.includes("deviceMaintenance")) {
    return <BuildIcon fontSize="small" />;
  }
  if (type.includes("workorder") || type.includes("work order")) {
    return <AutorenewIcon fontSize="small" />;
  }
  if (type.includes("inspection") || type.includes("new booking")) {
    return <CheckCircleIcon fontSize="small" />;
  }
  if (type.includes("booking")) {
    return <InfoOutlinedIcon fontSize="small" />;
  }
  return <NotificationsNoneIcon fontSize="small" />;
};

const NotificationDrawer = ({
  open,
  onClose,
  translate,
  onCountUpdate,
  serverTimeZone,
}) => {
  const [notifications, setNotifications] = useState([]);
  const [readFilter, setReadFilter] = useState(READ_FILTER.UNREAD);
  const [page, setPage] = useState(1);
  const [hasNext, setHasNext] = useState(false);
  const [loading, setLoading] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [markingId, setMarkingId] = useState(null);

  const fetchNotifications = useCallback(
    (pageNum = 1, reset = false) => {
      const isFirstPage = pageNum === 1;
      if (isFirstPage) {
        setLoading(true);
      } else {
        setLoadingMore(true);
      }

      instance({
        method: "GET",
        url: `/api/notifications/get1?page=${pageNum}&limit=${PAGE_LIMIT}&read=${readFilter}`,
        headers: {
          Accept: "application/json",
          "Content-Type": "application/json",
        },
      })
        .then((response) => {
          const items = Array.isArray(response?.data)
            ? response.data
            : Array.isArray(response)
              ? response
              : [];

          setNotifications((prev) =>
            reset || isFirstPage ? items : [...prev, ...items],
          );
          setPage(response?.page ?? pageNum);
          setHasNext(Boolean(response?.hasNext));
        })
        .catch(() => {
          if (reset || isFirstPage) {
            setNotifications([]);
          }
          setHasNext(false);
        })
        .finally(() => {
          setLoading(false);
          setLoadingMore(false);
        });
    },
    [readFilter],
  );

  const fetchCount = useCallback(() => {
    instance({
      method: "GET",
      url: "/api/notifications/count",
      headers: {
        Accept: "application/json",
        "Content-Type": "application/json",
      },
    })
      .then((response) => {
        const count =
          response?.count ??
          response?.unreadCount ??
          (typeof response === "number" ? response : 0);
        onCountUpdate?.(count);
      })
      .catch(() => {});
  }, [onCountUpdate]);

  useEffect(() => {
    if (open) {
      fetchNotifications(1, true);
      fetchCount();
    }
  }, [open, readFilter, fetchNotifications, fetchCount]);

  const handleMarkAsRead = (id) => {
    if (!id || markingId) return;

    setMarkingId(id);
    instance({
      method: "GET",
      url: `/api/notifications/markread/${id}`,
      headers: {
        Accept: "application/json",
        "Content-Type": "application/json",
      },
    })
      .then(() => {
        fetchNotifications(1, true);
        fetchCount();
      })
      .catch(() => {})
      .finally(() => {
        setMarkingId(null);
      });
  };

  const handleMarkAllAsRead = () => {
    instance({
      method: "GET",
      url: "/api/notifications/markreadall",
      headers: {
        Accept: "application/json",
        "Content-Type": "application/json",
      },
    })
      .then(() => {
        fetchNotifications(1, true);
        fetchCount();
      })
      .catch(() => {});
  };

  const handleLoadMore = () => {
    if (!hasNext || loadingMore) return;
    fetchNotifications(page + 1, false);
  };

  const handleFilterChange = (event) => {
    setReadFilter(event.target.value);
    setPage(1);
    setHasNext(false);
  };

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      className="notification-drawer"
    >
      <div className="notification-drawer-header">
        <div className="notification-drawer-title-wrap">
          <IconButton size="small" onClick={onClose} aria-label="close">
            <CloseIcon fontSize="small" />
          </IconButton>
          <h2 className="notification-drawer-title">
            {translate("reportEvents")}
          </h2>
        </div>

        <div className="notification-drawer-actions">
          <a
            href="#"
            className="notification-drawer-mark-all"
            onClick={(e) => {
              e.preventDefault();
              handleMarkAllAsRead();
            }}
          >
            {translate("markAllNotificationsAsRead")}
          </a>
          <FormControl size="small" className="notification-drawer-filter">
            <Select
              value={readFilter}
              onChange={handleFilterChange}
              displayEmpty
              renderValue={(selected) => {
                if (selected === READ_FILTER.READ) return translate("read");
                if (selected === READ_FILTER.UNREAD) return translate("unread");
                return translate("selectType");
              }}
            >
              <MenuItem value={READ_FILTER.ALL}>{translate("all")}</MenuItem>
              <MenuItem value={READ_FILTER.READ}>{translate("read")}</MenuItem>
              <MenuItem value={READ_FILTER.UNREAD}>
                {translate("unread")}
              </MenuItem>
            </Select>
          </FormControl>
        </div>
      </div>

      <div className="notification-drawer-body">
        {loading ? (
          <Loader defaultStyle />
        ) : notifications.length ? (
          <div className="notification-drawer-list">
            {notifications.map((item) => {
              const isRead = isNotificationRead(item);
              const accentColor = getAlertColor(item);
              const dateValue =
                item?.createdAt ||
                item?.notificationTime ||
                item?.date ||
                item?.eventTime ||
                item?.actionTime;

              return (
                <div
                  key={item.id}
                  className="notification-card"
                  style={getNotificationCardStyle(item)}
                >
                  <div
                    className="notification-card-icon"
                    style={{ backgroundColor: accentColor }}
                  >
                    <NotificationIcon item={item} />
                  </div>

                  <div className="notification-card-content">
                    <p className="notification-card-title">
                      {getNotificationTitle(item)}
                    </p>
                    <p className="notification-card-description">
                      {getNotificationDescription(item)}
                    </p>
                  </div>

                  <div className="notification-card-meta">
                    {!isRead && (
                      <div className="notification-card-checkbox">
                        <Checkbox
                          size="small"
                          disabled={markingId === item.id}
                          onChange={() => handleMarkAsRead(item.id)}
                        />
                      </div>
                    )}
                    {dateValue && (
                      <span className="notification-card-date">
                        {serverTimeZone
                          ? moment(dateValue)
                              .tz(serverTimeZone)
                              .format("YYYY-MM-DD HH:mm")
                          : getNotificationDate(item)}
                      </span>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        ) : (
          <div className="notification-drawer-empty">
            {translate("notFound")}
          </div>
        )}
      </div>

      {hasNext && !loading && (
        <div className="notification-drawer-footer">
          <Button
            className="notification-drawer-load-more button-dark"
            onClick={handleLoadMore}
            disabled={loadingMore}
          >
            {loadingMore ? translate("sharedLoading") : translate("loadMore")}
          </Button>
        </div>
      )}
    </Drawer>
  );
};

export default NotificationDrawer;
